
import { IApplicationContext } from "../../../ApplicationContext";
import { getDate, now } from "../../../dateUtils";
import fastStorage from "../../../fastStorage";
import { FmsSecTask } from "../../../fms.core/constants";
import { getOriginalValue } from "../../../fms.core/data/TrackingModule";
import { DepoDetail } from "../../../fms.core/DepoDetail";
import { DepoHd, PaymentGLInfo } from "../../../fms.core/DepoHd";
import { PaymentHistDetail } from "../../../fms.core/PaymentHistDetail";
import { FormType } from "../../../fms.enum";
import { SQLField, SQLInfo, SQLType } from "../../../framework.core/data/SQLInfo";
import { getJobNameFromInvBillCrDbJobType } from "../../../framework.core/JobNames";
import { getFmsGlobal } from "../../../globals";
import { clearHeaderFilters, hasActiveHeaderFilter } from "../../../ui/TableHeaderFilters";
import { getValueInCurrency } from "../../../util";

const fmsGlobal = getFmsGlobal();

// Header Filter 결과(oriAcctList의 복사본)에서 변경된 Line을 전체 목록에 반영
export function mergeFilteredAcctList(oriList: DepoDetail[], filteredList: DepoDetail[]): DepoDetail[] {
  const merged = oriList.map(ori => (ori.id !== 0 && filteredList.find(x => x.id === ori.id)) || ori);
  filteredList.forEach(x => {
    if (x.id !== 0 && !oriList.some(ori => ori.id === x.id)) merged.push(x);
  });
  return merged;
}

// getTotal에서 oriAcctList를 갱신할 때 사용
// Header Filter 적용 중에는 acctList가 필터 결과이므로 전체 목록(oriAcctList)을 덮어쓰지 않고 변경분만 반영
export function getOriAcctList(payment: DepoHd, currentUrl: string): DepoDetail[] {
  return hasActiveHeaderFilter(currentUrl)
    ? mergeFilteredAcctList(payment.oriAcctList ?? [], payment.acctList)
    : payment.acctList;
}

// Header Filter가 적용되어 있으면 필터를 해제하고 acctList를 전체 Line으로 복원 (저장 전에 호출)
export function restoreFilteredAcctList(payment: DepoHd | null, currentUrl: string, getTotal: () => void) {
  if (!payment || !hasActiveHeaderFilter(currentUrl)) return;

  const acctList = mergeFilteredAcctList(payment.oriAcctList ?? [], payment.acctList);
  clearHeaderFilters(currentUrl);

  payment.acctList = acctList;
  getTotal();

  // 필터로 숨겨졌을 때 deletedItems에 들어간 Line은 다시 표시되어도 제거되지 않으므로 직접 제거
  const ccProp = payment.changedCollectionProps.find(x => x.collectionName === 'acctList');
  if (ccProp) {
    ccProp.deletedItems = ccProp.deletedItems.filter(d => !acctList.some(x => x.id === d.keyValue));
  }
}

export class TBInfo {
  tbId: number = 0;
  tbName: string = "";

  constructor(init?: Partial<TBInfo>) {
    Object.assign(this, init);
  }
}

export enum PaymentGrid {
  Adv = 'Adv',
  Acct = 'Acct',
  Other = 'Other',
}

export interface PaymentParams {
  billTo?: number,
  billToName?: string,
  paidTo?: number,
  paidToName?: string,
  depositBank?: number,
  etf?: number,
  checkNo?: string,
  currency?: string,
  exRate?: number,
  exRateBank?: number,
  postDate?: string | null,
  depositDate?: string | null,
  remark?: string,
  selectedList?: AcctList[]
  id?: number,
};

export interface AcctList {
  tbId: number,
  tbName: string,
  payType: string,
  amount: any
}

class AdvDetail {
  branch: string = "";
  amount: number = 0;
  advRcvIncAmt: number = 0;
  advRcvDecAmt: number = 0;
  advPayIncAmt: number = 0;
  advPayDecAmt: number = 0;

  constructor(init?: Partial<AdvDetail>) {
    Object.assign(this, init);
  }
}
let _advRcvList: DepoDetail[] = [];
let _advPayList: DepoDetail[] = [];
let _paymentList: AdvDetail[] = [];

export async function addBankChargeRow(payment: DepoHd, appContext: IApplicationContext) {
  const bankChargeCode = appContext.suntekSettings?.accounting?.bankChargeAccountCode??"";
  // if (payment.acctList.length === 0) { return null; }  // 데이터가 없어도 Bank Charge를 표시
  if (payment.acctList.some(x=>x.glNo === bankChargeCode)) { return null; }   

  let bankChargeDesc = '';
  if(!payment.bankChargeDesc){
    bankChargeDesc = await appContext.api.getFieldValue({id: bankChargeCode, tableName: "T_CODEGLNO", fieldName: "F_GLDescription", idFieldName: "F_GLNO"})
  }

  const detail: DepoDetail = new DepoDetail({
    currency: payment.currency,
    glNo: bankChargeCode,
    glDescription: bankChargeDesc ? bankChargeDesc : payment.bankChargeDesc,
    branch: appContext.suntekSettings?.company?.enableBranch ? payment.depositBankBranch ?? '' : '',
    depoHdId: payment.id,
    accountName: bankChargeCode + ' - ' +(bankChargeDesc ? bankChargeDesc : payment.bankChargeDesc),
  });
  // detail.trackable = true;

  return detail;
}

export async function addOtherBankCharge(payment: DepoHd, appContext: IApplicationContext) {
  const bankChargeCode = appContext.suntekSettings?.accounting?.bankChargeAccountCode??"";
  if(payment.otherList.length === 0) { return null; }
  if(payment.otherList.some(x=>x.glNo === bankChargeCode)) { return null; }   

  if(!payment.bankChargeDesc){
    payment.bankChargeDesc = await appContext.api.getFieldValue({id: bankChargeCode, tableName: "T_CODEGLNO", fieldName: "F_GLDescription", idFieldName: "F_GLNO"})
  }

  const detail: DepoDetail = new DepoDetail({
    currency: payment.currency,
    glNo: bankChargeCode,
    glDescription: payment.bankChargeDesc,
    branch: appContext.user?.branch??'',
    sortNo: parseInt(bankChargeCode) + 1000000,
    accountName: bankChargeCode + ' - ' + payment.bankChargeDesc,    
  });

  return detail;
}

export function saveHistoryHeadData(sqlInfos: SQLInfo[], entity: DepoHd, strVoid: string = "", mode: FormType, userId: string, isReverse: boolean = false){
  let actionType = "";
  if([FormType.ModeAdd, FormType.ModeCopy, FormType.ModeRCopy].includes(mode)){
    actionType = "Create";
  }else if(mode === FormType.ModeEdit && !strVoid){
    actionType = "Change";
  }else if(mode === FormType.ModeEdit && strVoid){
    actionType = strVoid;
  }

  let payType = "Check";
  if (entity.etf === 1) payType = "W/T";
  else if (entity.etf === 3) payType = "ACH";
  else if (entity.etf === 4) payType = "Other";

  sqlInfos.push(new SQLInfo({
    tableName: "T_PAYHISTORY",
    type: SQLType.Insert,
    fields: [
      new SQLField({ fieldName: "F_HistoryDate", propertyName: "historyDate", value: now() }),
      new SQLField({ fieldName: "F_HistoryUser", propertyName: "historyUser", value: userId }),
      new SQLField({ fieldName: "F_HistoryAction", propertyName: "historyAction", value: actionType }),
      new SQLField({ fieldName: "F_BankID", propertyName: "bankID", value: entity.type === "M" ? entity.creditCardId : entity.depositBank }),
      new SQLField({ fieldName: "F_PostDate", propertyName: "postDate", value: entity.postDate }),
      new SQLField({ fieldName: "F_CheckNo", propertyName: "checkNo", value: entity.type === "M" ? entity.ccRefNo : entity.checkNo }),
      new SQLField({ fieldName: "F_Currency", propertyName: "currency", value: entity.currency }),
      new SQLField({ fieldName: "F_ExRate", propertyName: "exRate", value: entity.exRate }),
      new SQLField({ fieldName: "F_Block", propertyName: "block", value: entity.block }),
      new SQLField({ fieldName: "F_CustID", propertyName: "custID", value: entity.type === "M" ? 0 : entity.paidTo }),
      new SQLField({ fieldName: "F_TotalAmount", propertyName: "totalAmount", value: (isReverse ? -entity.tAmount : entity.tAmount) }),
      new SQLField({ fieldName: "F_DepoHDID", propertyName: "depoHDID", value: entity.id }),
      new SQLField({ fieldName: "F_Remark", propertyName: "remark", value: entity.remark }),
      new SQLField({ fieldName: "F_Draft", propertyName: "draft", value: entity.isDraft }),
      new SQLField({ fieldName: "F_BillTo", propertyName: "billTo", value: entity.billTo }),
      new SQLField({ fieldName: "F_Type", propertyName: "type", value: (isReverse ? (entity.type === 'D' ? 'C' : 'D') : (entity.isAgentPayment === 1 ? 'A' : entity.type)) }),
      new SQLField({ fieldName: "F_PayType", propertyName: "payType", value: payType }),
      //new SQLField({ fieldName: "F_CustName", propertyName: "custName",  value: entity.type === "M" ? "" : entity.paidToName }),
    ],
  }))
}

export function saveHistoryDetailData(sqlInfos: SQLInfo[], entity: DepoHd|null, detail: DepoDetail, type: string, grid: PaymentGrid, defaultBranch: string, bankChargeCode: string, isReverse: boolean = false){
  // isSwitch : C.Pay에서 Total이 음수여서 B.Pay로 저장해야 하는 경우 (반대 경우도) true로 설정하면 금액 부호가 반대로 해서 저장하기 위한 용도 

  if(!entity){ return; }
  let tempAmt: number = 0;

  let pDetail = new PaymentHistDetail({
    id: entity.GetNextId(),
    status: type,
    accountCode: detail.glNo,
    depoDetailId: detail.id
  });

  switch(grid){
    case PaymentGrid.Acct:
      pDetail.invoiceDate = detail.isInsertRow ? null : detail.postDate;
      pDetail.invoiceNo = detail.invNo;
      pDetail.description = detail.hblNo?.length > 50 ? detail.hblNo.substring(0, 50) : detail.hblNo;
      // <chuck date="2025-08-28" tag="MinusSave" desc="Total이 음수이면 B.Pay로 변경 로직 반영.">
      //pDetail.amount = type === "Deleted" ? getOriginalValue(detail, "amount") : detail.amount;
      pDetail.amount = (isReverse ? -1 : 1) * (type === "Deleted" ? getOriginalValue(detail, "amount") : detail.amount);
      // </chuck>
      pDetail.originalId = type === "Changed" ? detail.histID : 0;
      pDetail.depoDetailId = detail.id;
      if(detail.glNo === bankChargeCode || entity.type === "M") {   
        pDetail.customerId = entity.billTo;
      }else{
        pDetail.customerId = detail.entity;
      }
      pDetail.branch = detail.branch;
      pDetail.tbName = detail.tbName;
      pDetail.tbId = detail.tbId;
      break;
      
    case PaymentGrid.Adv:
      pDetail.invoiceDate = null;
      pDetail.invoiceNo = "";
      pDetail.description = detail.glDescription;
      if(detail.glNo === fmsGlobal.AdvPayGLNo){
        tempAmt = entity.advPayPaid;
      }else{
        tempAmt = entity.advRcvPaid;
      }
      //pDetail.amount = type === "Deleted" ? tempAmt : detail.increase - detail.decrease;
      pDetail.amount = type === "Deleted" ? tempAmt : detail.amount;
      pDetail.originalId = type === "Changed" ? detail.histID : 0;
      pDetail.depoDetailId = detail.id;
      pDetail.customerId = entity.billTo;
      pDetail.branch = "";
      pDetail.tbName = "";
      pDetail.tbId = 0;
      break;

    case PaymentGrid.Other:
      pDetail.invoiceDate = null;
      pDetail.invoiceNo = detail.invNo;
      pDetail.description = detail.description;

      // <chuck date="2025-08-28" tag="MinusSave" desc="Total이 음수이면 B.Pay로 변경 로직 반영.">
      //pDetail.amount = type === "Deleted" ? getOriginalValue(detail, "amount") : detail.amount;
      pDetail.amount = (isReverse ? -1 : 1) * (type === "Deleted" ? getOriginalValue(detail, "amount") : detail.amount);
      // </chuck>
      pDetail.originalId = type === "Changed" ? detail.histID : 0;
      pDetail.depoDetailId = detail.id;
      pDetail.customerId = entity.billTo;
      pDetail.branch = defaultBranch;
      pDetail.tbName = "";
      pDetail.tbId = 0;
      break;
  }

  sqlInfos.push(new SQLInfo({
    tableName: "T_PAYHISTORYDETAIL",
    type: SQLType.Insert,
    fields: [
      new SQLField({ fieldName: "F_HDID", propertyName: "HdId", value: pDetail.hdId }),
      new SQLField({ fieldName: "F_Status", propertyName: "Status", value: pDetail.status??"" }),
      new SQLField({ fieldName: "F_InvoiceDate", propertyName: "InvoiceDate", value: pDetail.invoiceDate }),
      new SQLField({ fieldName: "F_InvoiceNo", propertyName: "InvoiceNo", value: pDetail.invoiceNo??"" }),
      new SQLField({ fieldName: "F_GLNo", propertyName: "AccountCode", value: pDetail.accountCode??"" }),
      new SQLField({ fieldName: "F_Description", propertyName: "Description", value: pDetail.description??"" }),
      new SQLField({ fieldName: "F_Amount", propertyName: "Amount", value: pDetail.amount }),
      new SQLField({ fieldName: "F_OriginalID", propertyName: "OriginalId", value: pDetail.originalId }),
      new SQLField({ fieldName: "F_DepoDetailID", propertyName: "DepoDetailId", value: pDetail.depoDetailId }),
      new SQLField({ fieldName: "F_CustomerID", propertyName: "CustomerId", value: pDetail.customerId }),
      new SQLField({ fieldName: "F_Branch", propertyName: "Branch", value: pDetail.branch??"" }),
      new SQLField({ fieldName: "F_TBName", propertyName: "TBName", value: pDetail.tbName??"" }),
      new SQLField({ fieldName: "F_TBID", propertyName: "TBId", value: pDetail.tbId }),
      //new SQLField({ fieldName: "F_CustName", propertyName: "Customer", value: pDetail.customer})
    ] 
  }));
}

export function changeGrid(entity: DepoHd): boolean{
  entity.otherList.forEach(other =>{
    if(other.glNo !== getOriginalValue(other, "glNo")){
      return true;
    }
  });

  entity.advList.forEach(adv =>{
    if(adv.glNo === fmsGlobal.AdvRecGLNo && adv.increase - adv.decrease !== entity.advRcvPaid) {
      return true;
    }else if(adv.glNo === fmsGlobal.AdvPayGLNo && adv.increase - adv.decrease !== entity.advPayPaid){
      return true;
    }
  });

  entity.acctList.forEach(acct =>{
    if(acct.branch !== getOriginalValue(acct, "branch")){
      return true;
    }
  });

  return false;
}

export function saveAdvance(sqlInfos: SQLInfo[], entity: DepoHd, appContext: IApplicationContext){
  _advRcvList = [];
  _advPayList= [];
  _paymentList = [];

  //#region + VB Function : GetFromGrid
  const defaultBranch = appContext.suntekSettings?.company?.hqBranch??'';         // FmsGlobal.HQBranch
  const primaryCurrency = appContext.systemSettings?.primaryCurrency??'';         // FmsGlobal.MSetup.Currency
  const bankChargeCode = appContext.suntekSettings?.accounting?.bankChargeAccountCode??'';

  // + Advance 금액을 읽는다
  let fAdvRcvIncAmt = entity.advList.filter(x => x.glNo === fmsGlobal.AdvRecGLNo).reduce((sum: number, x) => sum + x.increase, 0);
  let fAdvRcvDecAmt = entity.advList.filter(x => x.glNo === fmsGlobal.AdvRecGLNo).reduce((sum: number, x) => sum + x.decrease, 0);
  let fAdvPayIncAmt = entity.advList.filter(x => x.glNo === fmsGlobal.AdvPayGLNo).reduce((sum: number, x) => sum + x.increase, 0);
  let fAdvPayDecAmt = entity.advList.filter(x => x.glNo === fmsGlobal.AdvPayGLNo).reduce((sum: number, x) => sum + x.decrease, 0);

  // + AR/AP 정보를 Branch 단위로 합산한다.
  entity.acctList.filter(x => x.amount !== 0).forEach(acct => {
    let payment = _paymentList.find(x => x.branch === acct.branch);
    if(!payment){
      const detail = new AdvDetail({
        branch: acct.branch,
        amount: acct.amount
      });
      _paymentList.push(detail);
    }else{
      payment.branch = acct.branch;
      payment.amount = payment.amount + acct.amount;
    }
  });

  if(_paymentList.length === 0){
    const detail = new AdvDetail({
      branch: defaultBranch
    });
    _paymentList.push(detail);
  }

  // + 합산 결과를 금액순으로 Sort 한다.
  _paymentList = _paymentList.sort((a, b) => a.amount - b.amount);  // 오름차순

  if(entity.type === "D"){
    // (+)금액
    if(fAdvRcvIncAmt > 0){
      const payment = _paymentList.find(x => x.branch === defaultBranch);
      if(payment){
        payment.advRcvIncAmt = fAdvRcvIncAmt;
      }else{
        const detail = new AdvDetail({
          branch: defaultBranch,
          advRcvIncAmt: fAdvRcvIncAmt
        });
        _paymentList.push(detail);
      }
    }
    // (+)금액
    if(fAdvPayDecAmt > 0){
      const payment = _paymentList.find(x => x.branch === defaultBranch);
      if(payment){
        payment.advPayDecAmt = fAdvPayDecAmt;
      }else{
        const detail = new AdvDetail({
          branch: defaultBranch,
          advPayDecAmt: fAdvPayDecAmt
        });
        _paymentList.push(detail);
      }
    }
    // (-)금액 : Branch별로 분배
    if(fAdvRcvDecAmt > 0){
      _paymentList.forEach(detail => {
        if(detail.amount > 0){
          const fTmpAmt = detail.amount > fAdvRcvDecAmt ? fAdvRcvDecAmt : detail.amount;
          detail.advRcvDecAmt = fTmpAmt;
          fAdvRcvDecAmt = fAdvRcvDecAmt - fTmpAmt;

          if(fAdvRcvDecAmt <= 0){
            return false;
          }
        }
      });
    }
  }
  else{
    // (+)금액
    if(fAdvPayIncAmt > 0){
      const payment = _paymentList.find(x => x.branch === defaultBranch);
      if(payment){
        payment.advPayIncAmt = fAdvPayIncAmt;
      }else{
        const detail = new AdvDetail({
          branch: defaultBranch,
          advPayIncAmt: fAdvPayIncAmt
        });
        _paymentList.push(detail);
      }
    }
    // (+)금액
    if(fAdvRcvDecAmt > 0){
      const payment = _paymentList.find(x => x.branch === defaultBranch);
      if(payment){
        payment.advRcvDecAmt = fAdvRcvDecAmt;
      }else{
        const detail = new AdvDetail({
          branch: defaultBranch,
          advRcvDecAmt: fAdvRcvDecAmt
        });
        _paymentList.push(detail);
      }
    }
    // (-)금액 : Branch별로 분배
    if(fAdvPayDecAmt > 0){
      _paymentList.forEach(detail => {
        if(detail.amount > 0){
          const fTmpAmt = detail.amount > fAdvPayDecAmt ? fAdvPayDecAmt : detail.amount;
          detail.advPayDecAmt = fTmpAmt;
          fAdvPayDecAmt = fAdvPayDecAmt - fTmpAmt;

          if(fAdvPayDecAmt <= 0){
            return false;
          }
        }
      });
    }
  }

  // + 계산된 결과를 기존 설정에 반영한다.
  let fBankBalance = entity.bankBalance;
  let fBankBalanceF = entity.bankBalanceF;

  let fTmpRcvBal = entity.currency !== primaryCurrency ? entity.advRcvdBalanceF : entity.advRcvdBalance;
  let fTmpPayBal = entity.currency !== primaryCurrency ? entity.advPymtBalanceF : entity.advPymtBalance;
  let fTmpRcvLocBal = entity.advRcvdBalance;
  let fTmpPayLocBal = entity.advPymtBalance;

  _paymentList.forEach(detail => {
    // Advance Received
    let fTmpAmt = detail.advRcvIncAmt - detail.advRcvDecAmt;
    if(entity.type === "C"){
      fTmpAmt = -fTmpAmt;
    }

    if(fTmpAmt !== 0){
      let advRcv = _advRcvList.find(x => x.branch === detail.branch);
      if(!advRcv){
        advRcv = new DepoDetail({
          branch: detail.branch,
          currency: entity.currency,
          glNo: fmsGlobal.AdvRecGLNo,
          glDescription: "ADVANCE RECEIVED",
          description: "ADVANCE RECEIVED",
          payType: "OT",
          depoHdId: entity.id
        });
        _advRcvList.push(advRcv);
      }

      advRcv.amount = fTmpAmt;
      if(entity.currency === primaryCurrency){
        advRcv.lAmount = fTmpAmt * entity.exRate;
      }else{
        advRcv.lAmount = fTmpAmt * (entity.type === "C" ? entity.exRateBank : entity.exRate);
      }

      if ((fTmpAmt < 0 || (entity.type === "C" && fAdvRcvDecAmt > 0)) && entity.advRcvdExRate !== 0){    //'[201010278]
        advRcv.exrateO = entity.advRcvdExRate;
        advRcv.oAmount = fTmpAmt * entity.exRate;

        if(fTmpRcvBal - fTmpAmt === 0){
          advRcv.oAmount = fTmpRcvLocBal;
        }else{
          advRcv.oAmount = fTmpAmt * entity.advRcvdExRate;
        }
      }else{
        advRcv.exrateO = entity.exRate;
        advRcv.oAmount = fTmpAmt * entity.exRate;
      }

      // Bank의 잔액이 0이되는 경우
      if (fBankBalance - advRcv.lAmount === 0){
        advRcv.amount = fBankBalanceF;
      }

      if (fBankBalanceF - fTmpAmt === 0){
        advRcv.lAmount = fBankBalance;
      }

      fTmpRcvBal = getValueInCurrency(fTmpRcvBal - advRcv.amount, entity.currency)??0;
      fTmpRcvLocBal = getValueInCurrency(fTmpRcvLocBal - advRcv.oAmount, primaryCurrency)??0;
      fBankBalanceF = getValueInCurrency(fBankBalanceF - advRcv.amount, entity.currency)??0;
      fBankBalance = getValueInCurrency(fBankBalance - advRcv.lAmount, primaryCurrency)??0;

      if (fTmpAmt > 0)
      {
        if (entity.type === "C"){
          advRcv.decrease = fTmpAmt;
        }
        else{
          advRcv.increase = fTmpAmt;
        }
      }
      else{
        advRcv.decrease = -fTmpAmt;
      }
    }

    // Advance Payable
    fTmpAmt = detail.advPayIncAmt - detail.advPayDecAmt;
    if (entity.type === "D"){
      fTmpAmt = -fTmpAmt;
    }

    if (fTmpAmt !== 0)
    {
      var advPay = _advPayList.find(x => x.branch === detail.branch);
      if (!advPay)
      {
        advPay = new DepoDetail({
          branch: detail.branch,
          currency: entity.currency,
          exrateO: entity.exRate,
          glNo: fmsGlobal.AdvPayGLNo,
          glDescription: "ADVANCE PAYMENT",
          description: "ADVANCE PAYMENT",
          payType: "OT",
          depoHdId: entity.id
        });
        _advPayList.push(advPay);
      }

      advPay.amount = fTmpAmt;
      if (entity.currency === primaryCurrency){
        advPay.lAmount = fTmpAmt * entity.exRate;
      }
      else{
        advPay.lAmount = fTmpAmt * (entity.type === "C" ? entity.exRateBank : entity.exRate);
      }

      if ((fTmpAmt < 0 || (entity.type === "D" && fAdvPayDecAmt > 0)) && entity.advPymtExRate !== 0){  //'[201010278]
        advPay.exrateO = entity.advPymtExRate;
        advPay.oAmount = fTmpAmt * entity.exRate;

        if (fTmpPayBal - fTmpAmt === 0){
          advPay.oAmount = fTmpPayLocBal;
        }
        else{
          advPay.oAmount = fTmpAmt * entity.advPymtExRate;
        }
      }
      else
      {
        advPay.exrateO = entity.exRate;
        advPay.oAmount = fTmpAmt * entity.exRate;
      }

      // Bank의 잔액이 0이되는 경우
      if (fBankBalance - advPay.lAmount === 0){
        advPay.amount = fBankBalanceF;
      }

      if (fBankBalanceF - fTmpAmt === 0){
        advPay.lAmount = fBankBalance;
      }

      fTmpPayBal = getValueInCurrency(fTmpPayBal - advPay.amount, entity.currency)??0;
      fTmpPayLocBal = getValueInCurrency(fTmpPayLocBal - advPay.oAmount, primaryCurrency)??0;
      fBankBalanceF = getValueInCurrency(fBankBalanceF - advPay.amount, entity.currency)??0;
      fBankBalance = getValueInCurrency(fBankBalance - advPay.lAmount, primaryCurrency)??0;

      if (fTmpAmt > 0)
      {
        if (entity.type === "D"){
          advPay.decrease = fTmpAmt;
        }
        else{
          advPay.increase = fTmpAmt;
        }
      }
      else{
        advPay.decrease = -fTmpAmt;
      }
    }
  });

  //#endregion

  //#region + VB Function : SaveAdanceToDB 
  
  // 초기에 읽어온 그리드를 기준으로 존재하지 않는 Branch는 제거
  for(let i = 0; i < entity.advDetailList.length; i++){
    const advRcv = _advRcvList.find(x => x.branch === entity.advDetailList[i].branch);
    if(!advRcv && fmsGlobal.AdvRecGLNo === entity.advDetailList[i].glNo){
      entity.advDetailList.splice(i, 1);
      if (entity.advDetailList.length) entity.advDetailList.length--;
      continue;
    }
    const advPay = _advPayList.find(x => x.branch === entity.advDetailList[i].branch);
    if(!advPay && fmsGlobal.AdvPayGLNo === entity.advDetailList[i].glNo){
      entity.advDetailList.splice(i, 1);
      if (entity.advDetailList.length) entity.advDetailList.length--;
      continue;
    }
  }
  // Advance Received : Add or Change
  _advRcvList.forEach(detail =>{
    const advRcv = entity.advDetailList.find(x => x.glNo === fmsGlobal.AdvRecGLNo && x.branch === detail.branch);
    if(!advRcv){
      entity.advDetailList.push(detail);
    }else{
      advRcv.amount = detail.amount;
      advRcv.glNo = detail.glNo;
      advRcv.glDescription = detail.glDescription;
      advRcv.description = detail.glDescription;
      advRcv.branch = detail.branch;
      advRcv.exrateO = detail.exrateO;
      advRcv.lAmount = detail.lAmount;
      advRcv.oAmount = detail.oAmount;
    }
  });
  // Advance Payment : Add or Change
  _advPayList.forEach(detail =>{
    const advPay = entity.advDetailList.find(x => x.glNo === fmsGlobal.AdvPayGLNo && x.branch === detail.branch);
    if(!advPay){
      entity.advDetailList.push(detail);
    }else{
      advPay.amount = detail.amount;
      advPay.glNo = detail.glNo;
      advPay.glDescription = detail.glDescription;
      advPay.description = detail.glDescription;
      advPay.branch = detail.branch;
      advPay.exrateO = detail.exrateO;
      advPay.lAmount = detail.lAmount;
      advPay.oAmount = detail.oAmount;
    }
  });

  entity.advDetailList.forEach(adv => {
    if(adv.id === 0){
      adv.id = entity.GetNextId();
    }
    let paid = 0;
    let oldPaid = 0;
    paid = adv.amount;

    if (fmsGlobal.AdvPayGLNo === adv.glNo){
      paid = -paid;
    }

    if (adv.glNo === fmsGlobal.AdvRecGLNo){
      oldPaid = entity.advRcvPaid;
    }
    else{
      oldPaid = -entity.advPayPaid;
    }

    if (paid !== oldPaid)
    {
      if (paid !== 0 && oldPaid === 0){
        saveHistoryDetailData(sqlInfos, entity, adv, "Added", PaymentGrid.Adv, defaultBranch, bankChargeCode);
      }
      else if (paid !== 0 && oldPaid !== 0){
        saveHistoryDetailData(sqlInfos, entity, adv, "Changed", PaymentGrid.Adv, defaultBranch, bankChargeCode);
      }
      else{
        saveHistoryDetailData(sqlInfos, entity, adv, "Deleted", PaymentGrid.Adv, defaultBranch, bankChargeCode);
      }
    }
  });

  //#endregion
}

export async function getRate(entity: DepoHd, appContext: IApplicationContext): Promise<string>{
  let errorMsg = "";
  entity.useBank = false;
  entity.bankExRate = 0;
  entity.bankBalance = 0;
  entity.bankBalanceF = 0;

  entity.advRcvdExRate = 0;
  entity.advRcvdBalance = 0;
  entity.advRcvdBalanceF = 0;

  entity.advPymtExRate = 0;
  entity.advPymtBalance = 0;
  entity.advPymtBalanceF = 0;
  
  if(entity.billTo > 0 || entity.depositBank > 0){
    const apiResult = await appContext.api.getRate(getDate(entity.postDate)??null, entity.depositBank, entity.currency, entity.billTo, entity.id, entity.type);
    if(apiResult){
      if(apiResult.success){
        const accountCodeInfos: PaymentGLInfo[] = apiResult.result;
        accountCodeInfos.forEach(codeInfo => {
          if(codeInfo.glType === "B"){
            entity.useBank = true;
            entity.bankExRate = codeInfo.exRate;
            entity.bankBalance = codeInfo.balance;
            entity.bankBalanceF = codeInfo.balanceF;
          }else{
            if(codeInfo.currency !== appContext.systemSettings?.primaryCurrency){
              if(codeInfo.glNo === fmsGlobal.AdvRecGLNo){
                entity.advRcvdExRate = codeInfo.exRate;
                entity.advRcvdBalance = codeInfo.balance;
                entity.advRcvdBalanceF = codeInfo.balanceF;
              }else if(codeInfo.glNo === fmsGlobal.AdvPayGLNo){
                entity.advPymtExRate = codeInfo.exRate;
                entity.advPymtBalance = codeInfo.balance;
                entity.advPymtBalanceF = codeInfo.balanceF;
              }else{
                entity.advRcvdExRate = 1;
                entity.advPymtExRate = 1;
              }
            }
          }
        });
      }
      else{
        errorMsg = apiResult.message;
      }
    }
  }
  return errorMsg;
}

export function changeRecord(entity: DepoHd){
  const postDate = getDate(entity.postDate)??"";
  entity.acctList.forEach(acct => {
    const acctDate = getDate(acct.postDate)??"";
    if(!postDate) { return; }
    if (acctDate) {
      if (acctDate > postDate) {
        if (acct.glNo === fmsGlobal.ARGLNo || acct.glNo === fmsGlobal.RcvGLNo || acct.glNo === fmsGlobal.GLNO_DUTYAR) {
          acct.glNo = fmsGlobal.AdvRecGLNo??"";
        } else if (acct.glNo === fmsGlobal.APGLNo || acct.glNo === fmsGlobal.PayGLNo || acct.glNo === fmsGlobal.DividendPayable || acct.glNo === fmsGlobal.GLNO_DUTYAP){
          acct.glNo = fmsGlobal.AdvPayGLNo??"";
        }
      } else {
        if (acct.glNo === fmsGlobal.AdvRecGLNo && !acct.stbName) {
          acct.glNo = fmsGlobal.RcvGLNo??"";
        }
        else if (acct.glNo === fmsGlobal.AdvRecGLNo && acct.stbName) {
          if (acct.payType === "DT") {
            acct.glNo = fmsGlobal.GLNO_DUTYAR??"";
          } else if (acct.payType === "DR") {
            acct.glNo = fmsGlobal.AgentGLNo??"";
          } else if (acct.payType === "D+") {
            acct.glNo = fmsGlobal.GLNO_DUTYAR_AGENT??"";
          } else {
            acct.glNo = fmsGlobal.ARGLNo??"";
          }
        } else if (acct.glNo === fmsGlobal.AdvPayGLNo && !acct.stbName) {
          if (acct.originGLNo === fmsGlobal.DividendPayable) {
            acct.glNo = fmsGlobal.DividendPayable??"";
          } else {
            acct.glNo = fmsGlobal.PayGLNo??"";
          }
        } else if (acct.glNo === fmsGlobal.AdvPayGLNo && acct.stbName) {
          if (acct.payType === "DT") {
            acct.glNo = fmsGlobal.GLNO_DUTYAP??"";
          } else if (acct.payType === "CR") {
            acct.glNo = fmsGlobal.AgentAPGLNo??"";
          } else if (acct.payType === "D-") {
            acct.glNo = fmsGlobal.GLNO_DUTYAP_AGENT??"";
          } else {
            acct.glNo = fmsGlobal.APGLNo??"";
          }
        }
      }
    }
  });
}

export async function getAccumulatedBalance(entity: DepoHd, oPostDate: string, paidAmount: number, accountCode: string, type: number, appContext: IApplicationContext){
  if(!entity.postDate) { return 0; }

  let balance: number = 0;
  let errMsg: string = "";

  const apiResult = await appContext.api.getAccumulatedBalance(
    entity.id > 0 ? entity.id : 0,
    entity.postDate,
    oPostDate > entity.postDate ? entity.postDate : oPostDate,
    now(),
    paidAmount,
    accountCode,
    entity.currency,
    entity.billTo,
    entity.type
  );

  if(apiResult && apiResult.success) {
    const result = apiResult.result;
    if(result){
      balance = parseFloat(result.F_Balance);
      if(balance < 0){
        switch (type){
          case 0:
            errMsg = `You cannot enter it because balance of ${result.F_GLDesc} becomes negative.`;
            break;
          case 1:
            errMsg = `You cannot change date because balance of ${result.F_GLDesc} becomes negative.`;
            break;
          case 2:
            errMsg = `You cannot void because balance of ${result.F_GLDesc} becomes negative.`;
            break;
        }
      }
    }
  }
  return errMsg;
}

export function checkItemHasEntered(entity: DepoHd, index: number = -1){
  let result = false;

  // //Operation Tab Grid에 값이 있는지 검사
  // if(index === 0){
  //   result = entity.acctList.some(x => x.amount !== 0);
  //   if(!result){
  //     result = entity.advList.some( x => (x.increase - x.decrease) !== 0);
  //   }
  // }

  // Operating Tab > Advance Grid 값이 있는지 검사
  if (index === 0) {
    result = entity.advList.some( x => (x.increase - x.decrease) !== 0);
  }
  // Non-Operating Tab Grid에 값이 있는지 검사
  else if (index === 1) {
    result = entity.otherList.some(x => x.amount !== 0);
  }
  // 전체 Grid에 값이 있는지 검사
  else {
    result = entity.acctList.some(x => x.amount !== 0);
    if (!result) 
      result = entity.advList.some(x => (x.increase - x.decrease) !== 0);
    if (!result)
      result = entity.otherList.some(x => x.amount !== 0);
  }
  return result;
}

export function setIsSelectForAcctList(acct: DepoDetail, tool: DepoDetail) {
  switch (acct.payType) {
    case "AR":
    case "TR":
    case "DR":
      if (tool.isSelectAR) {
        if (tool.dueAmtAR === 0) {
          acct.isSelect = true;
          acct.amount = acct.dueAmount + acct.amount;
        }
      } else {
        acct.isSelect = false;
        acct.amount = 0;
      }
      break;
    case "AP":
    case "TP":
    case "CR":
      if (tool.isSelectAP) {
        if (tool.dueAmtAP === 0) {
          acct.isSelect = true;
          acct.amount = acct.dueAmount + acct.amount;
        }
      } else {
        acct.isSelect = false;
        acct.amount = 0;
      }
      break;
    case "DT":
      if (acct.tbName === "T_INVOHD") {
        if (tool.isSelectDutyAR) {
          if (tool.dueAmtDutyAR === 0) {
            acct.isSelect = true;
            acct.amount = acct.dueAmount + acct.amount;
          }
        } else {
          acct.isSelect = false;
          acct.amount = 0;
        }
      } else if (acct.tbName === "T_APHD") {
        if (tool.isSelectDutyAP) {
          if (tool.dueAmtDutyAP === 0) {
            acct.isSelect = true;
            acct.amount = acct.dueAmount + acct.amount;
          }
        } else {
          acct.isSelect = false;
          acct.amount = 0;
        }
      }
      break;
    case "D+":
      if (tool.isSelectDutyAR) {
        if (tool.dueAmtDutyAR === 0) {
          acct.isSelect = true;
          acct.amount = acct.dueAmount + acct.amount;
        }
      } else {
        acct.isSelect = false;
        acct.amount = 0;
      }
      break;
    case "D-":
      if (tool.isSelectDutyAP) {
        if (tool.dueAmtDutyAP === 0) {
          acct.isSelect = true;
          acct.amount = acct.dueAmount + acct.amount;
        }
      } else {
        acct.isSelect = false;
        acct.amount = 0;
      }
      break;
    default:
      if (["T_PAYROLL", "T_PAYROLL2PAY", "T_DEPOHD"].includes(acct.tbName)) {
        if (tool.isSelectAP) {
          if (tool.dueAmtAP === 0) {
            acct.isSelect = true;
            acct.amount = acct.dueAmount + acct.amount;
          }
        } else {
          acct.isSelect = false;
          acct.amount = 0;
        }
      }
      break;
  }
}

export function getAccountingUrl(detail: DepoDetail): string {
  let linkBase = 'bill';
  if (detail.tbName === 'T_INVOHD') {
    linkBase = 'invoice';
  } else if (detail.tbName === 'T_CRDBHD') {
    linkBase = 'crdb';
  } else if (detail.tbName === 'T_TAXRTN') {
    return `/accounting/acSalesTaxReturn/view/${detail.tbId}`;
  } else if (detail.tbName === 'T_PAYROLL') {
    return `/accounting/acPayrollSummary/view/${detail.tbId}`;
  } else if (detail.tbName === 'T_PAYROLL2PAY') {
    return `/accounting/acPayroll2/view/${detail.mblId}`;
  } else if (detail.tbName === 'T_DEPOHD') {
    return `/accounting/acCCPayment/view/${detail.tbId}`;
  }

  if (!detail.stbName) {
    let linkBase = 'acGeneralBill';
    if(detail.tbName === 'T_INVOHD'){
      linkBase = 'acGeneralInvoice';
    }
    return `/accounting/${linkBase}/view/${detail.tbId}`;
  } else if (detail.stbName === 'WHP') {
    linkBase = 'periodBill';
    if(detail.tbName === 'T_INVOHD'){
      linkBase = 'periodInvoice';
    } else if (detail.tbName === 'T_CRDBHD') {
      linkBase = 'periodCrDb';
    }
    return `/accounting/${linkBase}/view/${detail.tbId}`;
  } else if (detail.stbName === 'STO') {
    return `/warehousing/storageInvoice/view/${detail.tbId}`;
  } else {
    let jobId = 0;
    const jobName = getJobNameFromInvBillCrDbJobType(detail.stbName);

    if (["T_OIMMAIN", "T_OOMMAIN", "T_AIMMAIN", "T_AOMMAIN", "T_GENMAIN", "T_WRECEIVE", "T_WSHIPHD"].includes(detail.mhName)) {
      jobId = detail.mblId;
    } else if (["T_OIHMAIN", "T_OOHMAIN", "T_AIHMAIN", "T_AOHMAIN"].includes(detail.mhName)) {
      jobId = detail.hblId;
    }

    if (jobId > 0) {
      return `/accounting/${linkBase}/view/${jobName}/${jobId}/${detail.tbId}`;
    }
  }
  return '';
}

export function moveStorage(prevKey: string, curKey: string) {
  fastStorage[curKey] = fastStorage[prevKey];
  fastStorage[prevKey] = null;
  delete fastStorage[prevKey];
}


export function getSecFromTbName(detail: DepoDetail): any {
  let jobSec = null;

  if (detail.tbName === 'T_INVOHD') {
    jobSec = FmsSecTask.AcctInvoiceList;
  } else if (detail.tbName === 'T_CRDBHD') {
    jobSec = FmsSecTask.AcctCrDbList;
  } else if (detail.tbName === 'T_APHD') {
    jobSec = FmsSecTask.AcctBillList;
  } else if (detail.tbName === 'T_TAXRTN') {
    return FmsSecTask.AcctSalesTaxReturnList;
  } else if (detail.tbName === 'T_PAYROLL' || detail.tbName === 'T_PAYROLL2PAY') {
    return FmsSecTask.AcctPayroll;
  }

  if (!detail.stbName) {
    jobSec = FmsSecTask.AcctGBillList;
    if (detail.tbName === 'T_INVOHD') {
      jobSec = FmsSecTask.AcctGInvoList;
    }
    return jobSec;
  } else if (detail.stbName === 'WHP') {
    jobSec = FmsSecTask.WHBill;
    if (detail.tbName === 'T_INVOHD') {
      jobSec = FmsSecTask.WHInvoice;
    } else if (detail.tbName === 'T_CRDBHD') {
      jobSec = FmsSecTask.WHCrDb;
    }
    return jobSec;
  } else if (detail.stbName === 'STO') {
    return FmsSecTask.WHStorageInv;
  }
  return jobSec;
}

export function getSecFromMhName(detail: DepoDetail): any {
  let jobSec = null;
  switch (detail.mhName) {
    case "T_OIMMAIN":
      jobSec = FmsSecTask.OIMbl;
      break;
    case "T_OIHMAIN":
      jobSec = FmsSecTask.OIHbl;
      break;
    case "T_OOMMAIN":
      jobSec = FmsSecTask.OEMbl;
      break;
    case "T_OOHMAIN":
      jobSec = FmsSecTask.OEHbl;
      break;
    case "T_AIMMAIN":
      jobSec = FmsSecTask.AIMbl;
      break;
    case "T_AIHMAIN":
      jobSec = FmsSecTask.AIHbl;
      break;
    case "T_AOMMAIN":
      jobSec = FmsSecTask.AEMbl;
      break;
    case "T_AOHMAIN":
      jobSec = FmsSecTask.AEHbl;
      break;
    case "T_GENMAIN":
      jobSec = FmsSecTask.OtherOP;
      break;
    case "T_WRECEIVE":
      jobSec = FmsSecTask.WHRcv;
      break;
    case "T_WSHIPHD":
      jobSec = FmsSecTask.WHShip;
      break;
    case "T_TaxReturnHD":
      jobSec = FmsSecTask.AcctSalesTaxReturnList;
      break;
    case "T_DepoHD":
      jobSec = FmsSecTask.AcctGBillList;
      break;
    default:
      break;
  }
  return jobSec;
}